import { useCallback, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { Link as RouterLink, Outlet } from 'react-router-dom';
import { Button, Tooltip } from '@fluentui/react-components';
import { Add20Regular, Search20Regular } from '@fluentui/react-icons';
import { Brand } from '@/components/layout/Brand';
import { GlobalSearch } from '@/components/layout/GlobalSearch';
import { MobileNav } from '@/components/layout/MobileNav';
import { SideNav } from '@/components/layout/SideNav';
import { UserMenu } from '@/components/layout/UserMenu';
import { OfflineBanner } from '@/components/feedback/OfflineBanner';
import { cn } from '@/lib/cn';
import { useIsCompact } from '@/hooks/useMediaQuery';
import { useSession } from '@/app/session';
import { useTeams } from '@/lib/teams/teams-context';

/**
 * Application shell.
 *
 * Structure: a 56px header (identity + search + actions + account), a collapsible
 * rail on desktop and a bottom bar on phones. The content column is the only
 * scroll container, so headers can be sticky without fighting the Teams iframe.
 */

const CONTENT_ID = 'contenido-principal';

export interface AppShellProps {
  /** Defaults to the routed `<Outlet />`. */
  children?: ReactNode;
  /** Primary action rendered in the header, e.g. "Nueva reservación". */
  onNewReservation?: () => void;
}

export function AppShell({ children, onNewReservation }: AppShellProps): JSX.Element {
  const { user, isAdmin } = useSession();
  const { isInTeams } = useTeams();
  const isCompact = useIsCompact();
  const [collapsed, setCollapsed] = useState(isCompact);
  const [searchOpen, setSearchOpen] = useState(false);

  // Narrow Teams windows start with the rail collapsed; the user can override.
  useEffect(() => setCollapsed(isCompact), [isCompact]);

  const toggleNav = useCallback(() => setCollapsed((current) => !current), []);

  const displayName = user?.displayName ?? 'Invitado';

  // Sin las pestañas de Teams, a un usuario normal sólo le quedaría "Inicio",
  // que ya es el logotipo del encabezado. Un riel de un solo destino es peor
  // que ninguno.
  const showRail = !isInTeams || isAdmin;

  return (
    <div className="flex app-viewport flex-col overflow-hidden bg-canvas text-ink">
      <a
        href={`#${CONTENT_ID}`}
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-3 focus:z-toast focus:rounded-md focus:bg-card focus:px-3 focus:py-2 focus:text-body focus:font-semibold focus:shadow-md"
      >
        Ir al contenido principal
      </a>

      <OfflineBanner />

      {/* The hairline under the header carries the amber of the mark: a brand
          detail, not a divider — the real separation is the border below it. */}
      <header className="relative z-header flex h-header shrink-0 items-center gap-2 border-b border-edge bg-card px-3 md:gap-4 md:px-4">
        <span aria-hidden="true" className="absolute inset-x-0 bottom-0 h-px bg-gold-500/70" />
        <RouterLink
          to="/"
          aria-label="Ir al inicio"
          className="shrink-0 rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus"
        >
          <Brand compact={isCompact} />
        </RouterLink>

        {/* Desktop: inline search. Phones: a toggle that reveals a full-width row. */}
        <div className="mx-auto hidden w-full max-w-xl md:block">
          <GlobalSearch />
        </div>

        <div className="ml-auto flex items-center gap-1.5 md:gap-2">
          <Tooltip content="Buscar" relationship="label" withArrow>
            <Button
              appearance="subtle"
              icon={<Search20Regular />}
              aria-label="Buscar"
              aria-expanded={searchOpen}
              className="md:hidden"
              onClick={() => setSearchOpen((current) => !current)}
            />
          </Tooltip>

          {onNewReservation ? (
            <Button appearance="primary" icon={<Add20Regular />} onClick={onNewReservation}>
              <span className="hidden sm:inline">Nueva reservación</span>
              <span className="sr-only sm:hidden">Nueva reservación</span>
            </Button>
          ) : null}

          <UserMenu
            name={displayName}
            email={user?.email ?? null}
            avatarUrl={user?.avatarUrl ?? null}
            isAdmin={isAdmin}
          />
        </div>
      </header>

      {searchOpen ? (
        <div className="shrink-0 border-b border-edge bg-card px-3 py-2 md:hidden">
          <GlobalSearch />
        </div>
      ) : null}

      <div className="flex min-h-0 flex-1">
        {/* Inside a Teams tab the host already draws Calendario, Mis
            reservaciones y Salas above the content. Repeating them in the rail
            asks the same question twice. What Teams does not offer — Inicio y
            la sección de administración — still needs somewhere to live, so the
            rail stays for whoever has that; for everyone else it disappears
            and the tab strip is the whole navigation. */}
        {showRail ? (
          <SideNav
            collapsed={collapsed}
            onToggle={toggleNav}
            isAdmin={isAdmin}
            hideTeamsTabs={isInTeams}
          />
        ) : null}

        <main
          id={CONTENT_ID}
          // 0, not -1: this element scrolls, and a scroll container that is
          // not in the tab order cannot be scrolled with the keyboard alone
          // (WCAG 2.1.1). It still receives focus from the skip link.
          tabIndex={0}
          className={cn('min-w-0 flex-1 overflow-y-auto overflow-x-hidden focus:outline-none')}
        >
          {children ?? <Outlet />}
        </main>
      </div>

      <MobileNav isAdmin={isAdmin} hideTeamsTabs={isInTeams} />
    </div>
  );
}
